<template>
  <div>
    <ShippingEligibilityInfo ref="eligibility" />
    <div v-loading="loading">
      <el-row
          :gutter="20"
          class="form-row">
        <el-col :span="24">
          <label>
            Reason Group
            <el-tooltip
                class="item"
                effect="dark"
                content="Refresh data"
                placement="top">
              <RefreshButton
                  class="flex-shrink"
                  style="padding: 0"
                  :text="false"
                  @click="refreshReasons" />
            </el-tooltip>
          </label>
          <el-input
              :value="getReasonGroup(request_reasons.reason_group).key | capitalize"
              readonly
              disabled
              style="margin: 5px 0px;" />
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="6">
          <el-input-number
              v-model="new_reason.quantity"
              :min="1"
              :max="10" />
        </el-col>
        <el-col :span="18">
          <el-select
              ref="reason"
              v-model="new_reason.id"
              style="width: 100%;"
              filterable
              clearable
              :disabled="currentReasons.length < 1"
              placeholder="Select part"
              @change="partShippingEligibility">
            <el-option
                v-for="reason in currentReasons"
                :key="reason.id"
                style="height: 3rem;"
                :disabled="partRestricted(reason.id) || reason.out_of_stock"
                :label="reason.text"
                :value="reason.id">
              <div class="part-option-row">
                <span>{{ reason.text }}</span>
                <span
                    v-if="reason.out_of_stock"
                    class="oos-inline">
                  — Out of stock
                </span>
                <span
                    v-else-if="reason.estimated_delivery"
                    class="estimated-delivery-inline">
                  — {{ reason.estimated_delivery }}
                </span>
              </div>
            </el-option>
          </el-select>
        </el-col>
      </el-row>
      <el-table
          style="width: 100%"
          :data="request_reasons.reasons">
        <el-table-column
            label="Quantity"
            width="150">
          <template slot-scope="scope">
            <el-tag>{{ scope.row.quantity }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Part">
          <template slot-scope="scope">
            {{ getReason(scope.row.id).text | capitalize }}
          </template>
        </el-table-column>
        <el-table-column
            label="Sku"
            width="120">
          <template slot-scope="scope">
            {{ getReason(scope.row.id).sku }}
          </template>
        </el-table-column>
        <el-table-column
            label="Estimated Delivery"
            width="200">
          <template slot-scope="scope">
            {{ scope.row.estimated_delivery || '—' }}
          </template>
        </el-table-column>
        <el-table-column width="100">
          <template slot-scope="scope">
            <el-button
                size="small"
                type="danger"
                @click="removeNewReason(scope.row)">
              Delete
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="center-buttons">
        <el-button
            plain
            @click="back()">
          Back
        </el-button>
        <el-button
            type="info"
            plain
            @click="reset()">
          Reset
        </el-button>
        <el-button
            :disabled="!isValid"
            type="primary"
            plain
            @click="next()">
          Next
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers as h } from "vuex";
import { get, findIndex, cloneDeep, assign } from "lodash";
import ShippingEligibilityInfo from "@/containers/CustomerRequest/CustomerRequestComponents/ShippingEligibilityInfo";
import { forEach } from "lodash/collection";
import RefreshButton from "@/containers/Customer/RefreshButton.vue";
import deliveryEstimates from "@/mixins/deliveryEstimates";

const { mapGetters: sharedGetters, mapActions: sharedActions } = h("shared");
const { mapActions: requestActions, mapGetters: requestGetters } =
  h("db/requests");

export default {
  name: "CustomerRequestProductStep",
  components: { RefreshButton, ShippingEligibilityInfo },
  mixins: [deliveryEstimates],
  props: {
    device: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },
  },
  data() {
    return {
      partsBlacklisted: [],
      shippingEligibility: {
        restrictedParts: [],
        validating: false,
        satisfied: true,
        errors: [],
      },
      valid: false,
      loading: false,
      currentReasons: [],
      new_reason: {
        quantity: 0,
        id: null,
      },
      request_reasons: {
        reason_group: null,
        reasons: [],
      },
    };
  },
  computed: {
    ...sharedGetters(["products", "reasons", "groups"]),
    ...requestGetters(["requestInProgress"]),
    isValid() {
      return (
        !!this.request_reasons.reason_group &&
        this.request_reasons.reasons.length > 0
      );
    },
    currentProduct() {
      return this.products.find((p) => p.kind === this.device.type);
    },
  },
  created() {
    this.refreshReasons();
    this.parseDataFromStore();
  },
  methods: {
    ...requestActions(["setProductInformation", "setReasonInformation"]),
    ...sharedActions(["fetchReasons"]),
    removeNewReason(selected) {
      let index = this.request_reasons.reasons.indexOf(selected);
      this.request_reasons.reasons.splice(index, 1);

      let reason = this.getReason(selected.id);
      this.currentReasons.push(reason);
    },
    partRestricted(reasonId) {
      return this.partsBlacklisted.includes(reasonId);
    },
    partShippingEligibility() {
      let reason =
        this.currentReasons.find((r) => r.id == this.new_reason.id) ||
        this.getReason(this.new_reason.id);
      let { customer_country: country_code, customer_state: state_code } =
        this.requestInProgress.address;
      this.$refs.eligibility
        .evaluate({
          part_descriptions: [reason.text],
          country_code,
          state_code,
        })
        .then((satisfied) => {
          if (satisfied) this.addNewReason(reason);
          else {
            this.new_reason = { quantity: 0, id: "" };
            this.partsBlacklisted.push(reason.id);
          }
        });
    },
    addNewReason(reason) {
      let index = this.currentReasons.indexOf(reason);
      this.currentReasons.splice(index, 1);

      const addedReason = this.new_reason;
      addedReason.sku = reason.sku;
      if (reason.estimated_delivery) {
        addedReason.estimated_delivery = reason.estimated_delivery;
      }
      this.request_reasons.reasons.push(addedReason);
      this.new_reason = { quantity: 0, id: "" };
    },
    parseDataFromStore() {
      this.initializePartList();
      forEach(this.requestInProgress.reasons.reasons, (reason) => {
        let toRemove = this.getReason(reason.id);
        let index = this.currentReasons.indexOf(toRemove);
        this.currentReasons.splice(index, 1);

        this.request_reasons.reasons.push(reason);
      });
    },
    next() {
      this.setReasonInformation(this.request_reasons);
      this.$emit("next");
    },
    reset() {
      this.new_reason = { quantity: 0, id: null };
      this.initializePartList();
    },
    initializePartList() {
      // get reason group
      let byRequestType = (group) =>
        group.key === this.requestInProgress.general.type.toLowerCase();

      let groupId = (this.groups.find(byRequestType) || {}).id;
      this.request_reasons.reason_group = groupId;

      // copy reasons for this group
      let anyPartForDevice = (kinds, device) =>
        kinds.indexOf(get(device, "kind", device.type)) > -1;

      const targetMarket = get(
        this.requestInProgress.address,
        "market",
        ""
      ).toLowerCase();

      let anyPartForMarket = (markets) => {
        if (!targetMarket) return true;
        return (
          findIndex(markets, {
            name: targetMarket,
            excluded: false,
          }) > -1
        );
      };

      this.currentReasons = this.reasons
        .filter((reason) => {
          let groupMatch = String(reason.reason_group_id) === String(groupId);
          // reason ~ Part; kinds ~ Device; market ~ address
          return (
            groupMatch &&
            anyPartForDevice(reason.kinds, this.device) &&
            anyPartForMarket(get(reason, "options.markets", []))
          );
        })
        .map((reason) => {
          let markets = get(reason, "options.markets", []);
          let market = markets.find((m) => {
            return m.name === targetMarket && m.out_of_stock === true;
          });

          let oos = get(market, "out_of_stock", false);
          return assign(cloneDeep(reason), { out_of_stock: oos });
        });

      const { customer_country, customer_zip } = this.requestInProgress.address;
      this.fetchDeliveryEstimates({
        country: customer_country,
        postalCode: customer_zip,
        items: this.currentReasons,
      });
    },
    refreshReasons() {
      this.fetchReasons()
        .then(() => console.log("success fetch reasons"))
        .catch(() => console.log("error fetch reasons"));
    },
    back() {
      this.$emit("back");
    },
    getReason(id) {
      return this.reasons.find((r) => r.id == id);
    },
    getReasonGroup(id) {
      return this.groups.find((r) => r.id == id) || {};
    },
  },
};
</script>

<style lang="scss">
.part-option-row {
  display: flex;
  align-items: center;
  gap: 4px;
  .oos-inline {
    color: #c0c4cc;
    font-size: 13px;
  }
  .estimated-delivery-inline {
    color: #6e6e6e !important;
    font-size: 13px;
    white-space: nowrap;
  }
}
</style>
